/*import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { registerPlugin } from '@capacitor/core';
import type { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { StorageService } from '../storage-service/storage.service';
import { firstValueFrom } from 'rxjs';
import { Network } from '@capacitor/network';

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>(
  'BackgroundGeolocation'
);

@Injectable({ providedIn: 'root' })
export class PosicionamentoService {
  private lastLat: number | null = null;
  private lastLng: number | null = null;
  private watcherId: string | null = null;
  private apiUrl: string = '';

  private storageService = inject(StorageService);

  constructor(private http: HttpClient) {
    this.initApiUrl();
  }

  private async initApiUrl() {
    try {
      const servidor = await this.storageService.getServidor();
      this.apiUrl = servidor?.url_servidor
        ? `${servidor.url_servidor}/posicionamento`
        : 'https://i9fluir.com.br:8082/api/posicionamento';
      ////console.log('API URL definida para:', this.apiUrl);
    } catch (error) {
      console.error('Erro ao buscar a URL da API no IndexedDB:', error);
      this.apiUrl = 'https://i9fluir.com.br:8082/api/posicionamento';
    }
  }

  private calcularDistancia(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLng = (lng2 - lng1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  async iniciarEnvio(
    motoristaId: number,
    veiculoId: number,
    trajetoId: number | null,
    horaInicial: string | null,
    horaFinal: string | null,
    data: string,
    distanciaPosicionamento: number
  ) {
    if (!horaInicial || horaFinal) return;
    if (!this.apiUrl) await this.initApiUrl();

    if (this.watcherId) {
      BackgroundGeolocation.removeWatcher({ id: this.watcherId });
      this.watcherId = null;
      this.lastLat = null;
      this.lastLng = null;
    }

    BackgroundGeolocation.addWatcher(
      {
        backgroundMessage: 'Enviando localização...',
        backgroundTitle: 'Jornada em andamento',
        requestPermissions: true,
        stale: false,
        distanceFilter: 0
      },
      async (location, err) => {
        if (err || !location) return;

        const lat = location.latitude;
        const lng = location.longitude;

        const distancia = this.lastLat !== null && this.lastLng !== null
          ? this.calcularDistancia(this.lastLat, this.lastLng, lat, lng)
          : Number.MAX_VALUE;

        if (distancia >= distanciaPosicionamento) {
          const horario = location.time
            ? new Date(location.time).toTimeString().split(' ')[0]
            : new Date().toTimeString().split(' ')[0];

          const payload = {
            data,
            horario,
            latitude: lat,
            longitude: lng,
            trajetoId,
            veiculoId,
            motoristaId
          };

          try {
            const status = await Network.getStatus();
            if (status.connected) {
              // tenta enviar
              await firstValueFrom(this.http.post(this.apiUrl, payload));
              ////console.log('✅ Posição enviada com sucesso (online)');
            } else {
              // salva localmente
              const id_local = `${Date.now()}-${Math.floor(Math.random()*1000)}`;
              await this.storageService.savePosicionamentoLocal({
                id_local,
                motoristaId,
                veiculoId,
                trajetoId,
                data,
                horario,
                latitude: lat,
                longitude: lng,
                sincronizado: false
              });
              ////console.log('📴 Sem internet — posição salva localmente', id_local);
            }
          } catch (e: any) {
            // em caso de erro no envio (timeout, 4xx/5xx), grava localmente para retry
            const id_local = `${Date.now()}-${Math.floor(Math.random()*1000)}`;
            await this.storageService.savePosicionamentoLocal({
              id_local,
              motoristaId,
              veiculoId,
              trajetoId,
              data,
              horario,
              latitude: lat,
              longitude: lng,
              tentativaEnvio: (e?.tentativaEnvio ?? 0) + 1,
              sincronizado: false
            });
            console.error('❌ Erro ao enviar posicionamento; salvo localmente:', e);
          }

          this.lastLat = lat;
          this.lastLng = lng;
        }
      }
    ).then(id => this.watcherId = id);
  }

  async pararEnvio() {
    if (!this.watcherId) return;

    if (this.lastLat !== null && this.lastLng !== null) {
      const horario = new Date().toTimeString().split(' ')[0];
      const payload = { 
        latitude: this.lastLat, 
        longitude: this.lastLng, 
        horario
      };

      try {
        const status = await Network.getStatus();
        if (status.connected) {
          await firstValueFrom(this.http.post(this.apiUrl, payload));
          ////console.log('✅ Última posição enviada antes de parar');
        } else {
          const id_local = `${Date.now()}-${Math.floor(Math.random()*1000)}`;
          await this.storageService.savePosicionamentoLocal({
            id_local,
            motoristaId: null,
            veiculoId: null,
            trajetoId: null,
            data: new Date().toISOString().split('T')[0],
            horario,
            latitude: this.lastLat,
            longitude: this.lastLng,
            sincronizado: false
          });
          ////console.log('📴 Última posição salva localmente (offline).');
        }
      } catch (e) {
        console.error('❌ Erro ao enviar última posição', e);
        // salvar localmente por segurança
        const id_local = `${Date.now()}-${Math.floor(Math.random()*1000)}`;
        await this.storageService.savePosicionamentoLocal({
          id_local,
          motoristaId: null,
          veiculoId: null,
          trajetoId: null,
          data: new Date().toISOString().split('T')[0],
          horario,
          latitude: this.lastLat,
          longitude: this.lastLng,
          sincronizado: false
        });
      }
    }

    BackgroundGeolocation.removeWatcher({ id: this.watcherId });
    this.watcherId = null;
    this.lastLat = null;
    this.lastLng = null;
    ////console.log('🛑 Envio de localização parado.');
  }

  async enviarPosicionamento(payload: any) {
    if (!this.apiUrl) {
      await this.initApiUrl();
    }
    return firstValueFrom(this.http.post(this.apiUrl, payload));
  }

}*/

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { registerPlugin } from '@capacitor/core';
import type { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { StorageService } from '../storage-service/storage.service';
import { firstValueFrom } from 'rxjs';
import { Network } from '@capacitor/network';

const BackgroundGeolocation =
  registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');

interface PosicionamentoPayload {
  motoristaId: number | null;
  veiculoId: number | null;
  trajetoId: number | null;
  data: string;
  horario: string;
  latitude: number;
  longitude: number;
}

@Injectable({ providedIn: 'root' })
export class PosicionamentoService {

  private watcherId: string | null = null;
  private lastLat: number | null = null;
  private lastLng: number | null = null;

  private storageService = inject(StorageService);

  constructor(private http: HttpClient) {}

  // 🔹 Sempre resolve URL antes de usar
  private async getApiUrl(): Promise<string> {
    try {
      const servidor = await this.storageService.getServidor();

      return servidor?.url_servidor
        ? `${servidor.url_servidor}/posicionamento`
        : 'https://i9fluir.com.br:8082/api/posicionamento';

    } catch {
      return 'https://i9fluir.com.br:8082/api/posicionamento';
    }
  }

  private calcularDistancia(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {

    const R = 6371000;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLng = (lng2 - lng1) * (Math.PI / 180);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLng / 2) ** 2;

    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  }

  private async salvarOffline(payload: PosicionamentoPayload) {

    const id_local = `${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    await this.storageService.savePosicionamentoLocal({
      id_local,
      ...payload,
      sincronizado: false
    });
  }

  async iniciarEnvio(
    motoristaId: number,
    veiculoId: number,
    trajetoId: number | null,
    horaInicial: string | null,
    horaFinal: string | null,
    data: string,
    distanciaMinima: number
  ) {

    if (!horaInicial || horaFinal) return;

    if (this.watcherId) {
      await BackgroundGeolocation.removeWatcher({ id: this.watcherId });
      this.resetWatcherState();
    }

    const apiUrl = await this.getApiUrl();

    this.watcherId = await BackgroundGeolocation.addWatcher(
      {
        backgroundMessage: 'Enviando localização...',
        backgroundTitle: 'Jornada em andamento',
        requestPermissions: true,
        stale: false,
        distanceFilter: 0
      },
      async (location, err) => {

        if (err || !location) return;

        const lat = location.latitude;
        const lng = location.longitude;

        const distancia =
          this.lastLat !== null && this.lastLng !== null
            ? this.calcularDistancia(this.lastLat, this.lastLng, lat, lng)
            : Number.MAX_VALUE;

        if (distancia < distanciaMinima) return;

        const horario = location.time
          ? new Date(location.time).toTimeString().split(' ')[0]
          : new Date().toTimeString().split(' ')[0];

        const payload: PosicionamentoPayload = {
          motoristaId,
          veiculoId,
          trajetoId,
          data,
          horario,
          latitude: lat,
          longitude: lng
        };

        try {
          const status = await Network.getStatus();

          if (status.connected) {
            await firstValueFrom(this.http.post(apiUrl, payload));
          } else {
            await this.salvarOffline(payload);
          }

        } catch {
          await this.salvarOffline(payload);
        }

        this.lastLat = lat;
        this.lastLng = lng;
      }
    );
  }

  async pararEnvio() {

    if (!this.watcherId) return;

    await BackgroundGeolocation.removeWatcher({ id: this.watcherId });

    this.resetWatcherState();
  }

  async enviarPosicionamento(payload: PosicionamentoPayload) {

    const apiUrl = await this.getApiUrl();
    return firstValueFrom(this.http.post(apiUrl, payload));
  }

  private resetWatcherState() {
    this.watcherId = null;
    this.lastLat = null;
    this.lastLng = null;
  }
}
